import { c as _c } from "react/compiler-runtime";
import { DateRangePicker, Hint, PrimaryButton } from '@systemeio/ui';
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface';
import { useApproveContactsImport } from 'modules/contacts/contacts-import/hook/use-approve-contacts-import';
import { useLastContactsImports } from 'modules/contacts/contacts-import/hook/use-last-contacts-imports';
import { ContactsImportStatus, ContactsLastImportId, ContactsLastImportsApiInterface, ContactsLastImportsInterface } from 'modules/contacts/contacts-import/types/contacts-import-interface';
import { getInvalidContactsLastImportAllData } from 'modules/contacts/utils/get-invalid-contacts-last-import-all-data-';
import { getInvalidContactsLastImportRows } from 'modules/contacts/utils/get-invalid-contacts-last-import-rows';
import React, { useEffect } from 'react';
import ConfirmationModalWithButton from 'shared/components/confirmation-modal/confirmation-modal-with-button';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { AdminRolesEnum } from 'shared/enums/user-roles-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { OrderEnum } from 'shared/types/pagination-interface';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getMonthDateRange, getToday } from 'shared/utils/get-dates';
import { KeyedMutator } from 'swr';
interface LastContactsImportsTableProps {
  className?: string;
  setMutateLastContactsImports: (mutate: KeyedMutator<ContactsLastImportsApiInterface>) => void;
}
const defaultFilters: ContactPageFilterInterface = {
  date: [null, null]
};
const LastContactsImportsTable = React.forwardRef<HTMLDivElement, LastContactsImportsTableProps>(function LastContactsImportsTable(t0, ref) {
  const $ = _c(100);
  const {
    className,
    setMutateLastContactsImports
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    checkAdminRoles,
    intlLocale,
    isSupportImpersonation
  } = useUser();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters, true);
  const {
    fetcherApproveContactsImport
  } = useApproveContactsImport();
  let t1;
  if ($[0] !== filter || $[1] !== limit || $[2] !== pagination) {
    t1 = {
      limit,
      pagination,
      filter
    };
    $[0] = filter;
    $[1] = limit;
    $[2] = pagination;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    lastContactsImports,
    mutateLastContactsImports,
    isLastContactsImportsValidating
  } = useLastContactsImports(t1);
  let t2;
  if ($[4] !== fetcherApproveContactsImport || $[5] !== mutateLastContactsImports) {
    t2 = async id => {
      await fetcherApproveContactsImport(id);
      await mutateLastContactsImports(_temp2);
    };
    $[4] = fetcherApproveContactsImport;
    $[5] = mutateLastContactsImports;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleApproveContactsImport = t2;
  let t3;
  if ($[7] !== t) {
    t3 = t("dashboard.contact_import.history.table.header.date");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== user?.dateFormat || $[10] !== user?.timeFormat || $[11] !== user?.timezone) {
    t4 = data_0 => <span>{dateTimeFormat(data_0.date, {
        timeFormat: user?.timeFormat,
        dateFormat: user?.dateFormat,
        timeZone: user?.timezone
      })}</span>;
    $[9] = user?.dateFormat;
    $[10] = user?.timeFormat;
    $[11] = user?.timezone;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = {
      key: "date",
      header: t3,
      widthPercent: 33,
      onRender: t4
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t) {
    t6 = t("dashboard.contact_import.history.table.header.registered");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = {
      key: "registered",
      header: t6,
      widthPercent: 33,
      onRender: _temp3
    };
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("dashboard.contact_import.history.table.header.status");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== checkAdminRoles || $[23] !== handleApproveContactsImport || $[24] !== isSupportImpersonation || $[25] !== t) {
    t9 = data_2 => {
      switch (data_2.status) {
        case ContactsImportStatus.approved:
          {
            return t("dashboard.contact_import.history.table.status.approved");
          }
        case ContactsImportStatus.failed:
          {
            return t("dashboard.contact_import.history.table.status.failed");
          }
        case ContactsImportStatus.suspended:
          {
            if (checkAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin, AdminRolesEnum.support]) || isSupportImpersonation) {
              return <ConfirmationModalWithButton onConfirm={() => handleApproveContactsImport(data_2.id)} title={t("dashboard.contact_import.history.table.approve_action.confirmation_dialog.title")} confirmationText={t("dashboard.contact_import.history.table.approve_action.confirmation_dialog.message")} renderButton={props => <PrimaryButton {...props}>{t("dashboard.contact_import.history.table.body.approve_button")}</PrimaryButton>} />;
            } else {
              return <Hint label={t("dashboard.contact_import.history.table.status.tooltip_for_suspended")}>{t("dashboard.contact_import.history.table.status.suspended")}</Hint>;
            }
          }
      }
    };
    $[22] = checkAdminRoles;
    $[23] = handleApproveContactsImport;
    $[24] = isSupportImpersonation;
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = {
      key: "status",
      header: t8,
      widthPercent: 33,
      onRender: t9
    };
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t) {
    t11 = t("dashboard.contact_import.history.table.header.invalid_data_hint");
    $[30] = t;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t) {
    t12 = t("dashboard.contact_import.history.table.header.invalid_data");
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t11 || $[35] !== t12) {
    t13 = <Hint label={t11}>{t12}</Hint>;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t) {
    t14 = t15 => {
      const {
        id: id_0,
        invalidContactsCount
      } = t15;
      return invalidContactsCount ? <a className="primary-link" download={true} href={getInvalidContactsLastImportRows(id_0)}>{t("dashboard.contact_import.history.table.header.invalid_data_download", {
          count: invalidContactsCount
        })}</a> : <p>{t("dashboard.contact_import.history.table.cell.no_invalid_rows")}</p>;
    };
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14) {
    t15 = {
      key: "invalidData",
      header: t13,
      widthPercent: 33,
      onRender: t14
    };
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== checkAdminRoles || $[43] !== isSupportImpersonation || $[44] !== t) {
    t16 = checkAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin, AdminRolesEnum.support]) || isSupportImpersonation ? [{
      key: "allData",
      header: t("dashboard.contact_import.history.table.header.all_data"),
      widthPercent: 33,
      onRender: t17 => {
        const {
          id: id_1
        } = t17;
        return <a className="primary-link" download={true} href={getInvalidContactsLastImportAllData(id_1)}>{t("dashboard.contact_import.history.table.body.all_data_download")}</a>;
      }
    } as ColumnDefinitionInterface<ContactsLastImportsInterface>] : [];
    $[42] = checkAdminRoles;
    $[43] = isSupportImpersonation;
    $[44] = t;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t10 || $[47] !== t15 || $[48] !== t16 || $[49] !== t5 || $[50] !== t7) {
    t17 = [t5, t7, t10, t15, ...t16];
    $[46] = t10;
    $[47] = t15;
    $[48] = t16;
    $[49] = t5;
    $[50] = t7;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const columns = t17;
  let t18;
  let t19;
  if ($[52] !== mutateLastContactsImports || $[53] !== setMutateLastContactsImports) {
    t18 = () => {
      setMutateLastContactsImports(mutateLastContactsImports);
    };
    t19 = [setMutateLastContactsImports, mutateLastContactsImports];
    $[52] = mutateLastContactsImports;
    $[53] = setMutateLastContactsImports;
    $[54] = t18;
    $[55] = t19;
  } else {
    t18 = $[54];
    t19 = $[55];
  }
  useEffect(t18, t19);
  let t20;
  if ($[56] !== setFilter || $[57] !== user?.timezone) {
    t20 = () => {
      setFilter({
        date: getMonthDateRange(user?.timezone)
      });
    };
    $[56] = setFilter;
    $[57] = user?.timezone;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  const t21 = user?.timezone;
  let t22;
  if ($[59] !== setFilter || $[60] !== t21) {
    t22 = [setFilter, t21];
    $[59] = setFilter;
    $[60] = t21;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  useEffect(t20, t22);
  const t23 = isLastContactsImportsValidating ? undefined : lastContactsImports;
  let t24;
  if ($[62] !== t) {
    t24 = t("dashboard.contact_import.history.table.title");
    $[62] = t;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  const t25 = user?.timezone;
  let t26;
  if ($[64] !== t25) {
    t26 = getToday(t25);
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== user?.timezone) {
    t27 = () => getMonthDateRange(user?.timezone);
    $[66] = user?.timezone;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== setFilter || $[69] !== setLimit || $[70] !== setPagination) {
    t28 = date => {
      setPagination({
        startFromId: undefined,
        order: OrderEnum.next
      });
      setLimit(10);
      setFilter({
        date
      });
    };
    $[68] = setFilter;
    $[69] = setLimit;
    $[70] = setPagination;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  const t29 = user?.timezone;
  const t30 = user?.createdAt;
  let t31;
  if ($[72] !== filter.date || $[73] !== intlLocale || $[74] !== t26 || $[75] !== t27 || $[76] !== t28 || $[77] !== t29 || $[78] !== t30) {
    t31 = <DateRangePicker maxDate={t26} getDefaultDate={t27} date={filter.date} onChange={t28} timezone={t29} createdAt={t30} intlLocale={intlLocale} />;
    $[72] = filter.date;
    $[73] = intlLocale;
    $[74] = t26;
    $[75] = t27;
    $[76] = t28;
    $[77] = t29;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== limit || $[81] !== setLimit) {
    t32 = {
      limit,
      setLimit
    };
    $[80] = limit;
    $[81] = setLimit;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== pagination || $[84] !== setPagination) {
    t33 = {
      pagination,
      setPagination
    };
    $[83] = pagination;
    $[84] = setPagination;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  const t34 = isLastContactsImportsValidating ? undefined : lastContactsImports;
  let t35;
  if ($[86] !== columns || $[87] !== t32 || $[88] !== t33 || $[89] !== t34) {
    t35 = <Table limiter={t32} paginator={t33} data={t34} columns={columns} />;
    $[86] = columns;
    $[87] = t32;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t23 || $[92] !== t24 || $[93] !== t31 || $[94] !== t35) {
    t36 = <ListingLayout counter={false} data={t23} type="default" title={t24} customHeaderElement={t31}>{t35}</ListingLayout>;
    $[91] = t23;
    $[92] = t24;
    $[93] = t31;
    $[94] = t35;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== className || $[97] !== ref || $[98] !== t36) {
    t37 = <div className={className} ref={ref}>{t36}</div>;
    $[96] = className;
    $[97] = ref;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  return t37;
});
export default LastContactsImportsTable;
function _temp(item) {
  return {
    ...item,
    status: ContactsImportStatus.approved
  };
}
function _temp2(data) {
  return {
    hasMore: data?.hasMore || false,
    items: data?.items.map(_temp) || []
  };
}
function _temp3(data_1) {
  return <span>{`${data_1.processedContactsCount}/${data_1.contactsCount}`}</span>;
}